import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import './Modal.css';

export interface ModalProps {
  /** Fecha o modal (Escape, backdrop, botão cancelar). */
  onClose: () => void;
  /** Título — vira <h2 id={titleId}> e cabeia o aria-labelledby do dialog. */
  title: ReactNode;
  /** Id do título (único por modal quando há mais de um na árvore). Default 'cf-modal-title'. */
  titleId?: string;
  children: ReactNode;
  /** Rodapé de ações (botões) alinhado à direita. */
  actions?: ReactNode;
  className?: string;
  /** Fecha ao clicar no backdrop (default true). */
  closeOnBackdrop?: boolean;
}

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Modal acessível: backdrop sólido (sem blur), `role="dialog"` + `aria-modal`,
 * FOCO PRESO (Tab/Shift+Tab circulam dentro), Escape fecha e o foco RETORNA ao
 * elemento que abriu o modal ao desmontar. Fecha o gap de a11y apontado no
 * design v2 (CofrePage não tinha trap nem retorno de foco).
 *
 * O app controla a montagem (renderize `<Modal>` só quando aberto). Superfície
 * chapada e de alto contraste — herda os tokens do tema (comitê/admin/recurso).
 */
export function Modal({
  onClose,
  title,
  titleId = 'cf-modal-title',
  children,
  actions,
  className,
  closeOnBackdrop = true,
}: ModalProps): JSX.Element {
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Guarda quem tinha o foco para devolver ao fechar (retorno de foco).
    restoreRef.current = (document.activeElement as HTMLElement | null) ?? null;
    const dialog = dialogRef.current;
    const focusables = dialog ? Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];
    (focusables[0] ?? dialog)?.focus();
    return () => {
      restoreRef.current?.focus?.();
    };
  }, []);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const f = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (f.length === 0) {
      e.preventDefault();
      dialog.focus();
      return;
    }
    const first = f[0];
    const last = f[f.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === dialog)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      className="cf-modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={cn('cf-modal', className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <h2 id={titleId} className="cf-modal__title">
          {title}
        </h2>
        <div className="cf-modal__body">{children}</div>
        {actions && <div className="cf-modal__actions">{actions}</div>}
      </div>
    </div>
  );
}
